import type { AxiosError } from 'axios';
import { ApiError, type ApiEnvelope, type Page } from '@/types/api';

/** Symbol-keyed stash for pagination metadata on list responses. */
export const PAGE_SYMBOL = Symbol('page');

export interface WithPage {
  [PAGE_SYMBOL]?: Page;
}

/**
 * Unwrap a backend response envelope.
 *
 * THREE shapes reach this function, because the services stack never settled on
 * one and this app was written against the monolith's:
 *
 *   1. `{ status: true, code, message, data, page }` — the monolith envelope,
 *      still served by identity-service's `/me/profile`.
 *   2. `{ success: true, data }` — the NestJS services' envelope
 *      (`/attractions`, `/admin/orders`, and most of ticketing).
 *   3. the payload itself, unwrapped — `/auth/login` returns
 *      `{ user, accessToken, expiresIn }` with no envelope at all.
 *
 * Only shape 1 used to be accepted, so every call this app made against the
 * services stack threw `ApiError` before its result was ever looked at — the
 * POS could not even log in. Handling all three here fixes all 14 api modules
 * at once rather than per call site.
 *
 * A missing `data` on an otherwise-successful envelope returns the body, since
 * some endpoints signal success with no payload.
 */
export function unwrap<T>(body: ApiEnvelope<T>): T {
  if (!body || typeof body !== 'object') {
    // A bare scalar/null body is the payload — there is nothing to unwrap.
    return body as T;
  }

  const ok = body.status === true || (body as { success?: boolean }).success === true;
  const failed = body.status === false || (body as { success?: boolean }).success === false;

  if (failed) {
    throw new ApiError({
      message: body?.message || 'Request failed',
      code: body?.code ?? 400,
      payload: body,
    });
  }

  // Shape 3: no envelope marker at all — the body IS the payload. Checked
  // before `data` so a payload that happens to carry its own `data` field
  // (an order with a `data` column, say) is not silently unwrapped one level
  // too far.
  if (!ok) return body as unknown as T;

  const data = ('data' in body ? body.data : (body as unknown as T)) as T;
  if (data && typeof data === 'object' && body.page) {
    try {
      (data as WithPage)[PAGE_SYMBOL] = body.page;
    } catch {
      /* frozen/primitive — ignore */
    }
  }
  return data;
}

/** Read pagination metadata stashed by {@link unwrap}. */
export function getPage(data: unknown): Page | undefined {
  if (data && typeof data === 'object') {
    return (data as WithPage)[PAGE_SYMBOL];
  }
  return undefined;
}

interface BackendErrorBody {
  code?: number;
  message?: string;
  errorCode?: string;
  blocked?: boolean;
  data?: { code?: number; message?: string; errorCode?: string };
}

/** Convert an axios error into a typed {@link ApiError}, preserving errorCode. */
export function toApiError(error: unknown): ApiError {
  const axiosErr = error as AxiosError<BackendErrorBody>;

  if (axiosErr?.code === 'ERR_NETWORK' || axiosErr?.message === 'Network Error') {
    return new ApiError({
      message: 'Tidak dapat terhubung ke server.',
      code: 0,
      isNetwork: true,
    });
  }

  const status = axiosErr?.response?.status ?? 500;
  const body = axiosErr?.response?.data;
  const nested = body?.data ?? body;

  return new ApiError({
    message: nested?.message || body?.message || axiosErr?.message || 'Terjadi kesalahan.',
    code: nested?.code ?? body?.code ?? status,
    errorCode: nested?.errorCode ?? body?.errorCode ?? null,
    payload: body,
  });
}
